// ------------- import external dependencies ------------
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BsPlusLg } from "react-icons/bs";

// ------------ import internal dependencies -------------
import EditDeleteComponent from "../../../../../components/EditDeleteComponent/EditDeleteComponent";
import Button from "../../../../../components/Button";
import useAssignment from "../../../../../hooks/useAssignment";
import { usePermission } from "../../../../../hooks/usePermission";
import useUser from "../../../hooks/useUser";
import {
  filterFacilities,
  filterGuestProfile,
  getPermissionObject,
  editORUpdate,
} from "../../../../../utils/helpers";
import { PERMISSION_LIST } from "../../../../../shared/sharedConstants";
import { Heading, AssWrapper, ColumnHeadingContainer } from "./style";
import { GlobalFilter } from "../../GlobalFilter";
import ReactSelectInput from "../../Fields/ReactSelectInput";
import MakeUserActiveOrInactive from "../../../../../components/RemoveUser/MakeUserActiveOrInactive";
import AlertDialog from "../../components/AlertDialog/AlertDialog";
import { v4 as uuidv4 } from "uuid";
import {
  useStoreWrapperHook,
  updateRow,
  getAssignmentTemplate,
  doHandleDeleteAssignment,
  PermissionModal,
  handleProfileOnChange,
  noEmailArr,
  onEditClick,
  onCancelClick,
  onDeleteClick,
  handleAssignmentData,
  ColumnContent,
  checkDisableBtn,
  checkDeleteDisabled,
  showMakeUserActiveOrInactive,
} from "./utils";

const RoleAssignment = () => {
  // ------- component state  managers -------
  const [openDelete, setOpenDelete] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [idToDelete, setIdToDelete] = useState("");
  const [tableData, setTableData] = useState<any>([]);
  const [profile, setProfile] = useState<any>(null);
  const [facility, setFacility] = useState<any>(null);
  const [role, setRole] = useState<any>(null);
  const [assignmentPermissions, setAssignmentPermissions] = useState<any>({});
  const [permissionList, setPermissionList] = useState<any>({});
  const [modalTitle, setModalTitle] = useState("");
  const [editing, setEditing] = useState(false);
  const [newAss, setNewAss] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [allowAddAssignment, setAllowAddAssignment] = useState(false);
  const prevFilteredTableDataLength = useRef(tableData.length);

  // ------ custom hooks intializations -------
  const location = useLocation();
  const navigate = useNavigate();

  const where = location.pathname;
  const { from = "" } = location?.state ?? {};
  const { isPermitted } = usePermission();

  const { selectedUser, setEdit, edit, user, userFacilities } =
    useStoreWrapperHook();

  const {
    deleteUserAssignment,
    getSystemFacilities,
    getSystemProfiles,
    getSystemRoles,
    addUserAssignment,
  } = useAssignment({
    profileId: profile?.profileId ?? "",
    userId: user?.userId,
  });

  const { updateFacilityData, getDetails } = useUser({
    id: where.includes("settings")
      ? user?.userId
      : (selectedUser?.userId ?? ""),
  });

  useEffect(() => {
    if (!where.includes("settings") && !selectedUser) navigate("/user/users");
  }, [where, selectedUser]);

  // ------- handle delete user request --------
  const { mutateAsync: deleteAssignment, isLoading: isDeleting } =
    deleteUserAssignment;

  // ------ handle add new assignment for user -------
  const { isLoading: addingAss, mutateAsync: addNewAssignment } =
    addUserAssignment;

  // ------- get user facilities --------
  const {
    // isLoading: facilitiesLoading,
    data: facilitiesResponse,
    refetch: refetchFacilities,
  } = getSystemFacilities;

  // ------- get system profiles --------
  const {
    // isLoading: profilesLoading,
    data: profilesData,
    refetch: refetchProfiles,
  } = getSystemProfiles;

  // -------- get system roles --------
  const {
    isLoading: rolesLoading,
    data: rolesData,
    // refetch: refetchRoles,
  } = getSystemRoles;

  useEffect(() => {
    if (userFacilities.length) {
      let truthy = false;
      userFacilities.map(
        (facility: any) => (truthy = noEmailArr.includes(facility.profileName))
      );
      setAllowAddAssignment(truthy);
    }
  }, [userFacilities]);

  const handleUpdateRow = (item: any, index: number) => {
    updateRow(item, index, {
      setTableData,
      setProfile,
      setFacility,
      setRole,
      setAssignmentPermissions,
      getPermissionObject,
      refetchFacilities,
      refetchProfiles,
      setPermissionList,
    });
  };

  /**
   * reset dependent state in
   */
  const resetStates = () => {
    setEditing(false);
    setEdit(false);
    setNewAss(false);
    setProfile(null);
    setRole(null);
    setFacility(null);
    setPermissionList({});
    setAssignmentPermissions({});
  };

  /**
   * Get user assignment in facilities
   */
  const {
    isSuccess: assSuccess,
    data: assData,
    refetch: refetchAss,
  } = getDetails;

  /**
   * User assignment template when adding new assignment
   */
  const assignmentTemplate = getAssignmentTemplate({
    setIdToDelete,
    setOpenDelete,
    user,
    updateFacilityData,
    setEdit,
    resetStates,
  });

  const getFacilityOptionLabel = useCallback(
    (option: any) => {
      return option?.facilityName;
    },
    [tableData]
  );

  const getFacilityOptionId = useCallback(
    (option: any) => {
      return option?.facilityId;
    },
    [tableData]
  );

  const handleFacilityFilterOption = useCallback(
    (option: any, inputValue: string) => {
      const isMatch = isPermitted(PERMISSION_LIST.ADD_USER, option?.value);
      const defaultFilter = option?.label
        .toLowerCase()
        .includes(inputValue.toLowerCase());
      return isMatch && defaultFilter;
    },
    [tableData, PERMISSION_LIST]
  );

  const handleFacilityOnChange = useCallback(
    (val: any) => {
      setFacility(val);
    },
    [tableData]
  );

  const getProfileOptionLabel = useCallback(
    (option: any) => {
      return option?.profileName;
    },
    [tableData]
  );

  const getProfileOptionId = useCallback(
    (option: any) => {
      return option?.profileId;
    },
    [tableData]
  );

  const handleProfileFilterOption = useCallback(
    (option: any, inputValue: string) => {
      const isEditGuest = !!(
        edit && option?.data?.profileName === "Guest User"
      );
      const defaultFilter = option?.label
        .toLowerCase()
        .includes(inputValue.toLowerCase());
      return !isEditGuest && defaultFilter;
    },
    [tableData]
  );

  const getRoleOptionLabel = useCallback(
    (option: any) => {
      return option?.roleName;
    },
    [tableData]
  );

  const getRoleOptionId = useCallback(
    (option: any) => {
      return option?.roleId;
    },
    [tableData]
  );

  const handleRoleOnchange = useCallback(
    (val: any) => {
      setRole(val);
      setModalTitle(val?.roleName);
      setOpenEdit(true);
    },
    [role, openEdit]
  );

  const addAssignment = useCallback(() => {
    setTableData((data: any) => [...data, assignmentTemplate]);
    setNewAss(true);
  }, [tableData, newAss]);

  const doCancelAddAssignment = useCallback(() => {
    setTableData((data: any) => {
      data.pop();
      return [...data];
    });
    setNewAss(false);
  }, [tableData, newAss]);

  const closePermissionListModal = useCallback(() => {
    setOpenEdit(false);
  }, [openEdit]);

  useEffect(() => {
    handleAssignmentData({
      assData,
      assSuccess,
      user,
      updateFacilityData,
      setEdit,
      resetStates,
      refetchAss,
      setIdToDelete,
      setOpenDelete,
      setTableData,
    });
  }, [assData]);

  const filteredTableData = tableData
    .map((element: any, index: number) => ({
      ...element,
      originalIndex: index,
    }))
    .filter((obj: any) => {
      return newAss
        ? obj
        : obj?.facility?.facilityName
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase());
    });

  const getMessageToDelete = () => {
    return filteredTableData
      .filter(
        (tableData: any) => tableData.assignment.assignmentId === idToDelete
      )
      .map((tableData: any) => tableData.assignment.facilityName)
      .join(", ");
  };

  useEffect(() => {
    if (tableData.length < prevFilteredTableDataLength.current) {
      resetStates();
    }
    prevFilteredTableDataLength.current = tableData.length;
  }, [tableData]);

  const isAddDisabled = useMemo(() => {
    return !facility || !profile || !role;
  }, [facility, profile, role]);

  const isButtonDisabled = (obj: any) => {
    if (obj.new) {
      return isAddDisabled;
    }
    return (
      checkDisableBtn({ obj, from, isPermitted, profile, role }) ||
      (selectedUser ? selectedUser?.status !== "Active" : false)
    );
  };

  return (
    <React.Fragment>
      <div className="mt-8">
        <div className="mb-8 w-1/2">
          <GlobalFilter
            type="text"
            half
            placeholder="Search Facility"
            searchValue={searchQuery}
            setSearchValue={setSearchQuery}
            disabled={newAss}
          />
        </div>
        <Heading>User Assignment</Heading>

        {/* ------- assignment wrapper section -------- */}
        <div className="mt-6">
          <AssWrapper>
            <ColumnHeadingContainer>
              <span style={{ fontSize: "16px" }} className="text-purple-200">
                Facility
              </span>
            </ColumnHeadingContainer>
            <ColumnHeadingContainer>
              <span style={{ fontSize: "16px" }} className="text-purple-200">
                Profile
              </span>
            </ColumnHeadingContainer>
            <ColumnHeadingContainer>
              <span style={{ fontSize: "16px" }} className="text-purple-200">
                Role
              </span>
            </ColumnHeadingContainer>
          </AssWrapper>

          {/* -------- user assignment list --------- */}
          {filteredTableData.map((obj: any, ind: number) => {
            return assSuccess ? (
              <AssWrapper key={obj.facilityId + ind}>
                <ColumnContent
                  obj={obj}
                  content={obj?.facility?.facilityName}
                  heading="Facility"
                >
                  <ReactSelectInput
                    name="facility"
                    options={filterFacilities(
                      tableData,
                      facilitiesResponse?.data?.data?.facilities
                    )}
                    getOptionLabel={getFacilityOptionLabel}
                    getOptionValue={getFacilityOptionId}
                    filterOption={handleFacilityFilterOption}
                    onChange={handleFacilityOnChange}
                    placeholder="Select Facility"
                    value={facility}
                  />
                </ColumnContent>

                <ColumnContent
                  obj={obj}
                  content={obj?.profile?.profileName}
                  heading="Profile"
                >
                  <ReactSelectInput
                    name="profile"
                    placeholder="Select Profile"
                    options={filterGuestProfile(
                      tableData.filter((ele: any) => {
                        return (
                          ele?.facility?.facilityName ===
                          obj?.facility?.facilityName
                        );
                      }),
                      profilesData?.data?.data
                    )}
                    getOptionLabel={getProfileOptionLabel}
                    getOptionValue={getProfileOptionId}
                    filterOption={handleProfileFilterOption}
                    onChange={(val: any, prevVal: any) =>
                      handleProfileOnChange({
                        val,
                        prevVal,
                        selectedUser,
                        tableData,
                        setProfile,
                        setRole,
                        setAssignmentPermissions,
                        getPermissionObject,
                        setPermissionList,
                      })
                    }
                    value={profile}
                  />
                </ColumnContent>

                <ColumnContent
                  obj={obj}
                  content={obj?.role?.roleName}
                  heading="Role"
                >
                  <ReactSelectInput
                    name="role"
                    placeholder="Select Role"
                    options={!rolesLoading ? rolesData?.data?.data : []}
                    getOptionLabel={getRoleOptionLabel}
                    getOptionValue={getRoleOptionId}
                    value={role}
                    onChange={handleRoleOnchange}
                  />
                </ColumnContent>

                <div>
                  <EditDeleteComponent
                    options={[
                      {
                        name: obj.new ? "Add" : editORUpdate(obj),
                        style: { color: "#a57fda" },
                        current: obj,
                        disabled: isButtonDisabled(obj),
                        select: async (assignment: any, current: any) => {
                          setEdit(true);
                          onEditClick({
                            obj,
                            assignment,
                            current,
                            user,
                            selectedUser,
                            profile,
                            role,
                            facility,
                            permissionList,
                            addNewAssignment,
                            resetStates,
                            refetchAss,
                            setTableData,
                            setNewAss,
                            from,
                            newAss,
                          });
                          handleUpdateRow(obj, obj.originalIndex);
                        },
                      },

                      {
                        name: "Delete",
                        style: "",
                        current: obj,
                        disabled: checkDeleteDisabled({
                          obj,
                          isPermitted,
                          selectedUser,
                        }),
                        select: (id: string) => {
                          onDeleteClick({
                            obj,
                            id,
                            setTableData,
                            resetStates,
                            refetchAss,
                          });
                        },
                      },

                      {
                        name: "Cancel",
                        style: {
                          display: obj.new || obj.edit ? "block" : "none",
                        },
                        current: obj,
                        select: () => {
                          onCancelClick({
                            obj,
                            doCancelAddAssignment,
                            refetchAss,
                          });
                        },
                      },
                    ]}
                    facilityId={obj?.facilityId}
                    assignment={obj?.assignment}
                    editing={editing || addingAss}
                  />
                </div>
              </AssWrapper>
            ) : (
              <span className="block text-center" key={uuidv4()}>
                No role Data
              </span>
            );
          })}
          {!newAss && !editing && (
            <div className="mt-8">
              {from !== "settings" &&
              !allowAddAssignment &&
              userFacilities?.length !== filteredTableData?.length &&
              (!selectedUser || selectedUser?.status !== "Active") ? null : (
                <Button addClass="text-purple-light mr-8" click={addAssignment}>
                  <BsPlusLg /> Add assignment
                </Button>
              )}
              {showMakeUserActiveOrInactive(where) && (
                <MakeUserActiveOrInactive
                  status={selectedUser?.status}
                  userId={selectedUser?.userId}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* -------- edit user permission list modal ------- */}
      <PermissionModal
        open={openEdit}
        close={closePermissionListModal}
        modalTitle={modalTitle}
        assignmentPermissions={assignmentPermissions}
        setPermissionList={setPermissionList}
      />

      {/* ------- delete user assignment  modal -------- */}
      {openDelete && (
        <AlertDialog
          message={`Delete Permissions for\n${getMessageToDelete()}`}
          onConfirm={() => {
            doHandleDeleteAssignment({
              loggedInUserId: user.userId,
              assignmentId: idToDelete,
              deleteAssignment,
              setOpenDelete,
              cb: refetchAss,
            });
          }}
          onCancel={() => {
            setOpenDelete(false);
          }}
          loading={isDeleting}
        />
      )}
    </React.Fragment>
  );
};

export default RoleAssignment;
